import React, { useCallback, useMemo } from "react";
import { Form, InputNumber, Select, Table } from "antd";
import { IMasterDataType } from "../../interfaces/master-data";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permission";

type NamePath = string | number | (string | number)[];

interface DynamicPaymentTableProps {
  name: NamePath;
  fieldKey?: "deposit" | "final" | "stages";
  rowCount?: number;
  useRowIndex?: boolean;
  eventTypes: IMasterDataType[];
  timings: IMasterDataType[];
  absoluteDrivers: IMasterDataType[];
  calculationBasis: IMasterDataType[];
  values?: any;
}

const DynamicPaymentTable: React.FC<DynamicPaymentTableProps> = ({
  name,
  fieldKey,
  rowCount = 1,
  useRowIndex = false,
  eventTypes = [],
  timings = [],
  absoluteDrivers = [],
  calculationBasis = [],
  values,
}) => {
  const hasSupplierEditor = useHasPermission([PERMISSIONS.SUPPLIER_EDITOR]);

  const data = useMemo(() => Array.from({ length: rowCount }, (_, i) => ({ key: i })), [rowCount]);

  const getName = (rowIndex: number, field: string): NamePath => {
    const base = Array.isArray(name) ? [...name] : [name];
    return [...base, ...(fieldKey ? [fieldKey] : []), ...(useRowIndex ? [rowIndex] : []), field];
  };
  const filterOption = (input: string, option?: { label: string }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const toOptions = (list: IMasterDataType[]) => list.map((item) => ({ value: item.code, label: item.label }));

  const renderSelect = (name: NamePath, options: any[], isDisabled: boolean = false) => (
    <Form.Item name={name} rules={[{ required: !isDisabled, message: "" }]}>
      <Select
        showSearch
        allowClear
        placeholder="Select"
        filterOption={filterOption}
        options={options}
        style={{ width: "100%" }}
        disabled={isDisabled || !hasSupplierEditor}
      />
    </Form.Item>
  );

  const renderNumber = (name: NamePath, isDisabled: boolean = false, max: number | undefined = undefined) => (
    <Form.Item name={name} rules={[{ required: !isDisabled, message: "" }]}>
      <InputNumber
        min={1}
        max={max}
        style={{ width: "100%" }}
        disabled={isDisabled || !hasSupplierEditor}
        suffix={max === 100 ? "%" : ""}
      />
    </Form.Item>
  );

  const isDisabledField = useCallback(
    (field: string, value: string, index: number) => {
      if (Array.isArray(values)) {
        return values?.[index]?.[field] === value;
      } else return values?.[field] === value;
    },
    [values]
  );

  const getMaxAmount = useCallback(
    (index: number) => {
      let isPercentage: boolean;
      if (Array.isArray(values)) {
        isPercentage = values?.[index]?.calculationBasis === "Percentage";
      } else isPercentage = values?.calculationBasis === "Percentage";
      return isPercentage ? 100 : undefined;
    },
    [values]
  );

  const isFinal = useCallback(() => fieldKey === "final" || name === "final", [fieldKey, name]);

  const columns = [
    {
      title: "Timing",
      render: (_: any, row: { key: number }) => renderSelect(getName(row.key, "timing"), toOptions(timings)),
    },
    {
      title: "Event Type",
      render: (_: any, row: { key: number }) => renderSelect(getName(row.key, "eventType"), toOptions(eventTypes)),
    },
    {
      title: "Duration (days)",
      render: (_: any, row: { key: number }) =>
        renderNumber(getName(row.key, "duration"), isDisabledField("timing", "On", row.key)),
    },
    {
      title: "Calculation Basis",
      render: (_: any, row: { key: number }) =>
        renderSelect(getName(row.key, "calculationBasis"), toOptions(calculationBasis), isFinal()),
    },
    {
      title: "Absolute Driver",

      render: (_: any, row: { key: number }) =>
        renderSelect(
          getName(row.key, "absoluteDriver"),
          toOptions(absoluteDrivers),
          isDisabledField("calculationBasis", "Percentage", row.key) || isFinal()
        ),
    },
    {
      title: "Amount",
      render: (_: any, row: { key: number }) =>
        renderNumber(getName(row.key, "amount"), isFinal(), getMaxAmount(row.key)),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data}
      pagination={false}
      size="small"
      className="table-fm"
      rowKey="key"
      tableLayout="fixed"
    />
  );
};

export default DynamicPaymentTable;
